<template>
  <BravadoSkeleton v-if="isLoading" size="sm" />
  <button
    v-else
    class="profile-selector-toggle"
    :class="{ [`profile-selector-toggle--${size}`]: true, 'profile-selector-toggle--with-hint': hint }"
    type="button"
    :disabled="isDisabled"
  >
    <template>
      <ProfileSelectorAvatar
        v-if="withAvatar"
        class="profile-selector-toggle__avatar"
        :name="name"
        :avatar-url="avatarUrl"
      />
      <BravadoText :class="textStyles" tag="p" :variant="mainTextVariant">
        {{ type }} as {{ name }}
        <AngleIcon width="20" height="20" />
      </BravadoText>
      <slot />
      <div v-if="hint" class="profile-selector-toggle__hint">
        <BravadoText class="profile-selector-toggle__hint-text" tag="p" variant="subhead-semibold">
          {{ hint }}
        </BravadoText>
      </div>
    </template>
  </button>
</template>

<script lang="ts" setup>
import { computed } from '@nuxtjs/composition-api';
import { SelectorType } from '@/components/partials/optional-anonymity/ProfileSelector/types';

const BravadoSkeleton = () => import('@/components/common/BravadoSkeleton/BravadoSkeleton.vue');
const AngleIcon = () => import('@/assets/svg/angle-down.vue.svg');
const ProfileSelectorAvatar = () =>
  import('@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorAvatar.vue');

type ProfileSelectorToggleProps = {
  withAvatar: boolean;
  isLoading: boolean;
  name: string;
  isDisabled?: boolean;
  hint?: string;
  avatarUrl?: string | null;
  size?: 'sm' | 'md';
  type?: SelectorType;
};

const props = withDefaults(defineProps<ProfileSelectorToggleProps>(), {
  isDisabled: false,
  hint: '',
  avatarUrl: null,
  size: 'sm',
  type: SelectorType.post,
});

const textStyles = computed(() => ({
  'profile-selector-toggle__name': true,
  'profile-selector-toggle__name--disabled': props.isDisabled,
}));

const mainTextVariant = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'subhead-semibold';
    case 'md':
      return 'body-semibold';
  }
});
</script>

<style lang="scss" scoped>
.profile-selector-toggle {
  $this: &;

  display: flex;
  align-items: center;
  appearance: none;
  outline: none;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: var(--theme-color-background);
  color: var(--theme-color-text);
  border-radius: 4px;

  &:disabled {
    cursor: not-allowed;
  }

  &--sm {
    padding: 4px 8px;
  }

  &--md {
    padding: 12px;
  }

  &--with-hint {
    position: relative;

    &:hover {
      #{$this}__hint {
        display: block;
      }
    }
  }

  &__avatar {
    margin-right: 8px;
  }

  &__name {
    display: flex;
    align-items: center;
    grid-gap: 4px;
    text-transform: capitalize;

    &:not(:last-child) {
      margin-right: 8px;
    }

    &--disabled {
      opacity: 0.3;
    }
  }

  &__hint {
    display: none;
    position: absolute;
    padding-top: 8px;
    width: 240px;
    top: calc(100% + 6px);
    left: calc(50% - 120px);

    &-text {
      position: relative;
      padding: 8px;
      color: var(--theme-color-card);
      background-color: var(--theme-color-text);
      border-radius: 8px;

      &::before {
        content: '';
        position: absolute;
        left: calc(50% - 6px);
        top: -6px;
        width: 12px;
        height: 12px;
        transform: rotate(45deg);
        background-color: var(--theme-color-text);
      }
    }
  }
}
</style>
